import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { PageLoader } from '@/components/common/PageLoader';
import { SubmitterDetails } from '@/components/common/SubmitterDetails';
import { cn } from '@/lib/utils';
import { instituteSubmissionDetailQueryOptions } from '../api/instituteSubmissions.api';
import { InstituteChangesSection } from '../components/InstituteChangesSection';
import { InstituteRemovalRequestSection } from '../components/InstituteRemovalRequestSection';
import { InstituteSubmissionCaptureSection } from '../components/InstituteSubmissionCaptureSection';
import { InstituteSubmissionLocationMap } from '../components/InstituteSubmissionLocationMap';
import { InstituteSubmissionReviewPanel } from '../components/InstituteSubmissionReviewPanel';
import { InstituteSubmissionStatusBadge } from '../components/InstituteSubmissionStatusBadge';
import { InstituteSubmissionTypeBadge } from '../components/InstituteSubmissionTypeBadge';
import { OpenInGoogleMapsButton } from '../components/OpenInGoogleMapsButton';
import { SeeAllSubmissionsButton } from '../components/SeeAllSubmissionsButton';
import {
  UNNAMED_INSTITUTE_TITLE,
  UPDATE_SUBMISSION_TYPE,
} from '../constants/instituteSubmission.constants';
import { useLastViewedSubmissionStore } from '../store/lastViewedSubmission.store';

const PANEL_CLASS = 'rounded-xl border bg-card shadow-xs';
const HEADER_BADGE_CLASS = 'font-semibold max-sm:h-6 max-sm:px-2 max-sm:text-xs-plus';

interface InstituteSubmissionDetailPageProps {
  submissionId: number;
}

export function InstituteSubmissionDetailPage({
  submissionId,
}: InstituteSubmissionDetailPageProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const {
    data: submission,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(instituteSubmissionDetailQueryOptions(submissionId));
  const setLastViewedSubmissionId = useLastViewedSubmissionStore(
    (state) => state.setLastViewedSubmissionId,
  );

  useEffect(() => {
    setLastViewedSubmissionId(submissionId);
  }, [submissionId, setLastViewedSubmissionId]);

  function handleBack() {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/settings/institute-submissions' });
  }

  function handleShowOnlyThisOfficer(userId: number) {
    void navigate({ to: '/settings/institute-submissions', search: { zmUserId: userId } });
  }

  const backButton = <BackButton onClick={handleBack} />;

  if (isLoading) return <PageLoader />;

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load this submission"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        action={backButton}
        fill
      />
    );
  }

  if (!submission) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm font-medium text-muted-foreground">Submission not found</p>
        {backButton}
      </div>
    );
  }

  const submissionTitle = submission.current.name ?? UNNAMED_INSTITUTE_TITLE;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <header className="flex flex-col gap-2">
        <PageTitle leading={backButton}>{submissionTitle}</PageTitle>
        <PageToolbar
          currentTitle={submissionTitle}
          breadcrumbAddon={
            <>
              <InstituteSubmissionTypeBadge
                submissionType={submission.submissionType}
                className={HEADER_BADGE_CLASS}
              />
              <InstituteSubmissionStatusBadge
                status={submission.status}
                className={HEADER_BADGE_CLASS}
              />
            </>
          }
        >
          <SeeAllSubmissionsButton
            userId={submission.userId}
            onShowOnlyThisOfficer={handleShowOnlyThisOfficer}
            className="max-sm:h-8"
          />
        </PageToolbar>
        <SubmitterDetails
          name={submission.userName}
          hrEmpId={submission.hrEmpId}
          amName={submission.amName}
          submittedAt={submission.submittedAt}
        />
      </header>

      <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_18rem] xl:grid-cols-[minmax(0,1fr)_20rem] 3xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div
          className={cn(
            PANEL_CLASS,
            'relative flex flex-col overflow-hidden lg:max-h-full lg:min-h-0 lg:self-start',
          )}
        >
          <div className="flex flex-col gap-6 p-4 lg:min-h-0 lg:overflow-y-auto">
            {submission.submissionType === UPDATE_SUBMISSION_TYPE ? (
              <InstituteChangesSection
                code={submission.code}
                current={submission.current}
                proposed={submission.proposed}
              />
            ) : (
              <InstituteRemovalRequestSection submission={submission} />
            )}
            <InstituteSubmissionReviewPanel submission={submission} />
          </div>
        </div>

        <div
          className={cn(
            PANEL_CLASS,
            'flex flex-col gap-3 p-3 lg:max-h-full lg:min-h-0 lg:self-start lg:overflow-y-auto',
          )}
        >
          <InstituteSubmissionCaptureSection submission={submission} />
          <div className="aspect-4/3 w-full shrink-0 overflow-hidden rounded-lg border">
            <InstituteSubmissionLocationMap submission={submission} />
          </div>
          <OpenInGoogleMapsButton latitude={submission.latitude} longitude={submission.longitude} />
        </div>
      </div>
    </div>
  );
}
